import { CommonModule } from '@angular/common';
import { Component, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AgendaGradeLinha } from '../../../../interfaces/agenda';
import { AgendaModaisService } from '../../../../services/agenda/agenda-modais.service';
import { AgendaService, mensagemErroAgenda } from '../../../../services/agenda/agenda.service';
import { AgendaIgnorado, corpoCancelar, elegibilidadeCancelamento } from '../agenda-acoes';

/** O back corta em 200 (CD_MOTIVO 99 + texto no DS_OBSERVACAO). */
export const MOTIVO_CANCELAMENTO_MAX = 200;

export interface ResultadoCancelamento {
  linha: AgendaGradeLinha;
  ok: boolean;
  mensagem: string;
}

/** Cancela um ou vários (só Normal e Confirmada), uma chamada por agendamento, com motivo obrigatório. */
@Component({
  selector: 'app-agenda-cancelar',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './agenda-cancelar.component.html',
  styleUrl: './agenda-cancelar.component.scss',
})
export class AgendaCancelarComponent implements OnInit, OnDestroy {
  @Input({ required: true }) linhas!: AgendaGradeLinha[];

  @Output() fechar = new EventEmitter<void>();
  /** Terminou de cancelar (com ou sem erro): a página recarrega a grade. */
  @Output() concluido = new EventEmitter<void>();

  readonly motivoMax = MOTIVO_CANCELAMENTO_MAX;

  aptas: AgendaGradeLinha[] = [];
  ignorados: AgendaIgnorado[] = [];
  motivo = '';
  tentou = false;
  salvando = false;
  resultados: ResultadoCancelamento[] | null = null;

  private fecharRegistro: (() => void) | null = null;

  constructor(private api: AgendaService, private modais: AgendaModaisService) {}

  ngOnInit() {
    this.fecharRegistro = this.modais.abrir();
    ({ aptas: this.aptas, ignorados: this.ignorados } = elegibilidadeCancelamento(this.linhas));
  }

  ngOnDestroy() {
    this.fecharRegistro?.();
    this.fecharRegistro = null;
  }

  @HostListener('document:keydown.escape')
  aoEsc() {
    if (!this.modais.coberto()) this.cancelar();
  }

  cancelar() {
    if (!this.salvando) this.fechar.emit();
  }

  motivoIgnorado(l: AgendaGradeLinha): string | null {
    return this.ignorados.find(i => i.linha === l)?.motivo ?? null;
  }

  get semMotivo(): boolean { return !this.motivo.trim(); }

  get okCount(): number { return (this.resultados ?? []).filter(r => r.ok).length; }
  get erroCount(): number { return (this.resultados ?? []).filter(r => !r.ok).length; }

  async confirmar() {
    if (this.salvando || this.resultados || !this.aptas.length) return;
    this.tentou = true;
    if (this.semMotivo) return;
    this.salvando = true;
    this.resultados = [];
    try {
      for (const l of this.aptas) {
        try {
          const r = await this.api.cancelar(l.nrSequencia, corpoCancelar(l, this.motivo));
          this.resultados.push({ linha: l, ok: true, mensagem: r.mensagem || 'Agendamento cancelado com sucesso.' });
        } catch (e) {
          this.resultados.push({ linha: l, ok: false, mensagem: mensagemErroAgenda(e, 'Erro ao cancelar o agendamento.') });
        }
      }
    } finally {
      this.salvando = false;
      this.concluido.emit();
    }
  }
}
